import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { demoService } from "../../services/demo/demoService";
import { useColorMode } from "../../contexts/AppThemeProvider";
import { useAuth } from "../../contexts/AuthContext";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Drawer,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Tooltip,
  useTheme,
  alpha,
  Stack,
  Chip,
} from "@mui/material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkIcon from "@mui/icons-material/Work";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import BugReportIcon from "@mui/icons-material/BugReport";
import FeedbackDialog from "../feedback/FeedbackDialog";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ApiIcon from "@mui/icons-material/Api";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import GroupsIcon from "@mui/icons-material/Groups";
import BuildIcon from "@mui/icons-material/Build";
import SettingsIcon from "@mui/icons-material/Settings";
import FlagIcon from "@mui/icons-material/Flag";
import ShareIcon from "@mui/icons-material/Share";
import { onboardingService } from "../../services/onboarding/onboardingService";
import { useShareAction } from "../../contexts/ShareActionContext";
import InvitationsBell from "../groups/InvitationsBell";

interface NavigationProps {
  currentPage:
    | "dashboard"
    | "expenses"
    | "income"
    | "debts"
    | "goals"
    | "accounts"
    | "transactions"
    | "trends"
    | "groups"
    | "tools"
    | "settings";
  apiConnected?: boolean;
  onToggleApi?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({
  currentPage,
  apiConnected,
  onToggleApi,
}) => {
  const [isDemoMode, setIsDemoMode] = useState(demoService.isDemo());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { mode, toggleColorMode } = useColorMode();
  const { user, signOut } = useAuth();
  const theme = useTheme();
  const routerLocation = useLocation();
  const { hasShareAction, openShare } = useShareAction();

  const navItems = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: <DashboardIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/income",
      label: "Income",
      icon: <AttachMoneyIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/expenses",
      label: "Expenses",
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/debts",
      label: "Debts",
      icon: <CreditCardIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/goals",
      label: "Goals",
      icon: <FlagIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/transactions",
      label: "Transactions",
      icon: <ReceiptLongIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/trends",
      label: "Trends",
      icon: <TrendingUpIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/accounts",
      label: "Accounts",
      icon: <AccountBalanceIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/groups",
      label: "Groups",
      icon: <GroupsIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/tools",
      label: "Tools",
      icon: <BuildIcon sx={{ fontSize: 18 }} />,
    },
    {
      path: "/settings",
      label: "Settings",
      icon: <SettingsIcon sx={{ fontSize: 18 }} />,
    },
  ];

  useEffect(() => {
    // Subscribe to demo mode changes
    const unsubscribe = demoService.subscribe((isDemo) => {
      setIsDemoMode(isDemo);
    });

    return unsubscribe;
  }, []);

  const handleDemoToggle = () => {
    demoService.toggleDemoMode();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleMenuClose();
    await signOut();
  };

  const handleRestartOnboarding = async () => {
    await onboardingService.resetOnboarding();
    window.location.reload(); // Reload to trigger onboarding check
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const getUserDisplayName = () => {
    if (user?.email) {
      return user.email.split("@")[0]; // Get username part of email
    }
    return "User";
  };

  const getInitials = () => {
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase();
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ flex: 1, ml: 3 }}
      justifyContent="space-between"
    >
      {/* Navigation Links - Hidden on mobile */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          display: { xs: "none", md: "flex" },
        }}
      >
        {navItems.map((item) => {
          const isActive =
            routerLocation.pathname === item.path ||
            (item.path !== "/expenses" &&
              routerLocation.pathname.startsWith(`${item.path}/`));
          return (
            <Tooltip
              key={item.path}
              title={item.label}
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#000000",
                    color: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    py: 0.75,
                    px: 1.5,
                    "& .MuiTooltip-arrow": {
                      color: "#000000",
                    },
                  },
                },
              }}
            >
              <IconButton
                component={Link}
                to={item.path}
                sx={{
                  color: isActive
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  backgroundColor: isActive
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                  borderRadius: 2,
                  width: 40,
                  height: 40,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isActive
                      ? alpha(theme.palette.primary.main, 0.15)
                      : alpha(theme.palette.action.hover, 0.08),
                    transform: "scale(1.05)",
                  },
                }}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          );
        })}
      </Stack>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            background:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.98)
                : alpha("#ffffff", 0.98),
            width: 280,
            borderLeft: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Menu
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Focus: Finance Navigation
            </Typography>
          </Box>
          <IconButton onClick={handleMobileMenuClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ py: 0.5 }}>
          {navItems.map((item) => {
            const isActive =
              routerLocation.pathname === item.path ||
              (item.path !== "/expenses" &&
                routerLocation.pathname.startsWith(`${item.path}/`));
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={isActive}
                  onClick={handleMobileMenuClose}
                  sx={{
                    borderRadius: 1.5,
                    mx: 1,
                    my: 0.25,
                    color: isActive
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    "&.Mui-selected": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 700 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      {/* Demo Mode Chip */}
      {isDemoMode && (
        <Chip
          label="Demo Mode"
          size="small"
          icon={<TheaterComedyIcon />}
          onClick={handleDemoToggle}
          sx={{
            background: `linear-gradient(135deg, ${alpha(
              "#9F7AEA",
              0.2
            )} 0%, ${alpha("#805AD5", 0.2)} 100%)`,
            color: "#9F7AEA",
            border: `1px solid ${alpha("#9F7AEA", 0.3)}`,
            fontWeight: 600,
            "&:hover": {
              background: `linear-gradient(135deg, ${alpha(
                "#9F7AEA",
                0.3
              )} 0%, ${alpha("#805AD5", 0.3)} 100%)`,
            },
          }}
        />
      )}

      {/* Action Icons */}
      <Stack direction="row" spacing={0.5}>
        {hasShareAction && (
          <Tooltip title="Share">
            <IconButton
              onClick={openShare}
              size="small"
              sx={{
                display: { xs: "none", md: "inline-flex" },
                color: theme.palette.text.secondary,
                background: alpha(theme.palette.action.hover, 0.05),
                "&:hover": {
                  background: alpha(theme.palette.action.hover, 0.1),
                  transform: "scale(1.05)",
                },
              }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <InvitationsBell />
        {/* Demo Mode Toggle (when not in demo mode) */}
        {!isDemoMode && (
          <Tooltip title="Switch to Demo Mode">
            <IconButton
              onClick={handleDemoToggle}
              size="small"
              sx={{
                display: { xs: "none", md: "inline-flex" },
                color: theme.palette.text.secondary,
                background: alpha(theme.palette.action.hover, 0.05),
                "&:hover": {
                  background: alpha(theme.palette.action.hover, 0.1),
                  transform: "scale(1.05)",
                },
              }}
            >
              <WorkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Getting Started */}
        <Tooltip title="Restart Getting Started">
          <IconButton
            onClick={handleRestartOnboarding}
            size="small"
            sx={{
              display: { xs: "none", md: "inline-flex" },
              color: theme.palette.text.secondary,
              background: alpha(theme.palette.action.hover, 0.05),
              "&:hover": {
                background: alpha(theme.palette.action.hover, 0.1),
                transform: "scale(1.05)",
              },
            }}
          >
            <PlayCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Dark Mode Toggle */}
        <Tooltip title={mode === "dark" ? "Light Mode" : "Dark Mode"}>
          <IconButton
            onClick={toggleColorMode}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              background: alpha(theme.palette.action.hover, 0.05),
              "&:hover": {
                background: alpha(theme.palette.action.hover, 0.1),
                transform: "scale(1.05)",
              },
            }}
          >
            {mode === "dark" ? (
              <Brightness7Icon fontSize="small" />
            ) : (
              <Brightness4Icon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="API Docs">
          <IconButton
            component={Link}
            to="/api-docs"
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              background: alpha(theme.palette.action.hover, 0.05),
              "&:hover": {
                background: alpha(theme.palette.action.hover, 0.1),
                transform: "scale(1.05)",
              },
            }}
          >
            <ApiIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* User Menu */}
        <Tooltip title="Account">
          <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{
              ml: 1,
              padding: 0,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                fontSize: "0.875rem",
                fontWeight: 600,
                boxShadow: `0 2px 8px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: `0 4px 12px ${alpha(
                    theme.palette.primary.main,
                    0.4
                  )}`,
                },
              }}
            >
              {getInitials()}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Stack>

      {/* User Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            mt: 1.5,
            minWidth: 200,
            background:
              mode === "dark"
                ? alpha(theme.palette.background.paper, 0.95)
                : alpha("#ffffff", 0.98),
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor:
                mode === "dark" ? theme.palette.background.paper : "#ffffff",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderBottom: "none",
              borderRight: "none",
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {getUserDisplayName()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleMenuClose} component={Link} to="/settings">
          <PersonIcon fontSize="small" sx={{ mr: 1.5 }} />
          Profile Settings
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setFeedbackOpen(true);
          }}
        >
          <BugReportIcon fontSize="small" sx={{ mr: 1.5 }} />
          Send Feedback
        </MenuItem>
        <MenuItem onClick={handleSignOut}>
          <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
          Sign Out
        </MenuItem>
      </Menu>

      <Tooltip title="Open menu">
        <IconButton
          onClick={handleMobileMenuOpen}
          size="small"
          sx={{
            display: { xs: "inline-flex", md: "none" },
            color: theme.palette.text.secondary,
            ml: 1,
          }}
        >
          <MenuIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
    </Stack>
  );
};

export default Navigation;
